"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    CAS_RENVOYES,
    DES_DELAI,
    DES_PORTAIL,
    SEUIL_ACHATS_BIENS,
    euros,
    numeroExige,
    obligations,
    recapitulatif,
    seuilDuCas,
    situation,
    valider,
} from "@/lib/tools/crossBorderInvoicing";
import { jourDu, mentionsAutoliquidationAdmises } from "@/lib/tools/vatMentions";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Liste,
    Recommencer,
    Resultat,
    remplir,
    useParcours,
} from "./Journey";

/**
 * Facturer et acheter à l'étranger, en micro-entreprise.
 *
 * TROIS QUESTIONS AVANT D'ÉDITER LA FACTURE : professionnel ou particulier,
 * dans l'Union ou en dehors, un bien ou un service. Le parcours les pose dans
 * cet ordre parce que c'est celui dans lequel elles s'excluent.
 *
 * LE PARCOURS SAIT SE TAIRE. Trois situations ne sont couvertes par aucune
 * source officielle utilisable, et l'outil les renvoie au service des impôts au
 * lieu d'inventer une règle : le client hors Union, la vente de biens à un
 * professionnel européen, et la vente de services à un particulier européen.
 * C'est un écran de refus, pas un résultat vide.
 *
 * LE PIÈGE LE PLUS CHER EST À L'ACHAT, et c'est pour cela que la première
 * question demande le SENS de l'opération. Un abonnement logiciel européen fait
 * du micro-entrepreneur le redevable de la TVA française et l'oblige à déposer
 * une déclaration, malgré la franchise.
 */

const L = {
    fr: {
        phases: ["Votre opération", "Votre client", "Ce qui s'impose"],
        retour: "Retour",
        prive: "Rien ne sort de cet onglet : le diagnostic se fait dans votre navigateur, et tout s'efface quand vous le fermez.",

        sensTitre: "Vous vendez, ou vous achetez ?",
        sensAide: "Les règles ne sont pas symétriques : ce que vous devez à la vente et ce que vous devez à l'achat n'ont ni les mêmes seuils ni les mêmes déclarations.",
        vends: "Je vends",
        vendsDetail: "Une prestation ou un produit à un client à l'étranger",
        achete: "J'achète",
        acheteDetail: "Un service ou des marchandises auprès d'un fournisseur étranger",

        zoneTitre: "Où votre interlocuteur est-il établi ?",
        zoneAide: "« Étranger » et « européen » ne sont pas la même chose, et c'est la confusion la plus fréquente du sujet.",
        ue: "Dans l'Union européenne",
        ueDetail: "Allemagne, Belgique, Espagne, Irlande, Italie…",
        horsUe: "En dehors de l'Union",
        horsUeDetail: "Suisse, Royaume-Uni, États-Unis, Canada…",

        clientTitre: "Votre client est-il un professionnel ?",
        clientAide: "Un professionnel autoliquide la taxe chez lui. Un particulier ne le peut pas, et les règles changent entièrement.",
        pro: "Un professionnel",
        proDetail: "Une entreprise, avec un numéro de TVA",
        particulier: "Un particulier",
        particulierDetail: "Un consommateur, sans numéro de TVA",

        natureTitre: "Un bien, ou une prestation de services ?",
        natureTitreAchat: "Vous achetez un bien, ou un service ?",
        natureAide: "Les seuils ne sont pas les mêmes : les services entre professionnels n'en ont aucun.",
        service: "Une prestation de services",
        serviceDetail: "Conseil, développement, design, formation, abonnement logiciel…",
        bien: "Un bien",
        bienDetail: "Une marchandise, un produit physique, du stock",

        cumulTitre: "Combien avez-vous cumulé cette année ?",
        cumulAideAchat: "Le total de vos achats de biens dans l'Union depuis le 1er janvier. Le seuil de {seuil} se compte sur l'année civile, pas par commande.",
        cumulAideVente: "Le total de vos ventes à des particuliers d'autres États membres depuis le 1er janvier. Le seuil de {seuil} se compte sur l'année civile.",
        champCumul: "Cumul depuis le 1er janvier",
        erreurFormat: "Indiquez un montant en euros, par exemple 9500.",

        renvoiTitre: "Cette situation se vérifie auprès de votre service des impôts",
        renvoiHorsUe: "Un client suisse, britannique, américain ou canadien n'est pas un client européen. Ni le numéro de TVA intracommunautaire ni la déclaration européenne de services ne le concernent, et les règles applicables varient selon la nature de l'opération et le pays. Aucune source officielle n'en donne un exposé général utilisable : faites confirmer votre cas par votre service des impôts des entreprises, avant d'émettre la facture plutôt qu'après.",
        renvoiVenteBiensPro: "L'administration fiscale recense trois situations européennes : vos achats de biens, vos prestations de services entre professionnels, et vos ventes à distance à des particuliers. La vente de biens à une entreprise d'un autre État membre n'y figure pas, et aucune source consultée n'en donne le régime pour un vendeur en franchise. Faites confirmer votre cas avant d'éditer la facture.",
        renvoiVenteServicesParticulier: "L'administration fiscale recense trois situations européennes : vos achats de biens, vos prestations de services entre professionnels, et vos ventes à distance de biens à des particuliers. Une prestation de services vendue à un particulier européen n'y figure pas. Faites confirmer votre cas par votre service des impôts des entreprises avant d'émettre la facture.",
        renvoiNote: "Nous préférons vous dire que nous ne savons pas plutôt que de vous donner une règle que nous ne pouvons pas fonder.",
        voirResultat: "Voir ce qui s'impose",
        resultatTitre: "Ce que votre situation impose",
        recapTitre: "Le détail",
        recommencer: "Recommencer",

        numeroOui: "Numéro de TVA intracommunautaire obligatoire",
        numeroNon: "Pas encore de numéro de TVA à demander",

        alerteSeuilProche: "Vous approchez du seuil : il vous reste {reste} avant de le franchir. Le cumul se compte sur l'année civile, et une seule commande peut suffire à le passer.",

        mentionsTitre: "Les mentions admises",
        mentions: "« Autoliquidation » est la formule que retient l'administration, mais ce n'est pas la seule : la doctrine accepte aussi la référence à l'article 196 de la directive 2006/112/CE, celle à l'article 283-2 du code général des impôts qui la transpose, admise jusqu'au 30 juin 2028 comme tout renvoi à ce code, ou toute autre mention indiquant que l'opération relève de l'autoliquidation.",

        desTitre: "La déclaration que personne ne réclame",
        des: "La déclaration européenne de services se dépose sur le portail de la douane, jamais sur impots.gouv.fr, et au plus tard le 10e jour ouvrable du mois suivant. Elle n'a aucun seuil : une facture de 300 € l'exige au même titre qu'une facture de 30 000 €. Elle est mensuelle, mais seulement pour les mois où vous avez facturé une prestation de ce type. Et ce n'est pas une déclaration de TVA, c'est un état récapitulatif.",

        achatTitre: "Ce que la franchise ne vous épargne pas",
        achat: "L'administration écrit qu'un micro-entrepreneur n'a aucune déclaration de TVA à déposer « en règle générale ». Cette réserve porte précisément les opérations européennes. À l'achat, vous devenez le redevable de la TVA française, et vous devez la déclarer même en franchise, même sans avoir encaissé le moindre euro de taxe.",

        suiteTitre: "Et maintenant",
        suite1: "Le numéro de TVA intracommunautaire se demande à votre service des impôts des entreprises, par la messagerie sécurisée de votre espace professionnel, et il arrive en quelques jours. Demandez-le avant d'éditer la facture : il n'est pas attribué d'office tant que vous êtes en franchise.",
        suite2: "Vérifiez le numéro de votre client avant d'éditer la facture : c'est lui qui justifie que la taxe part chez lui.",
        suite3: "Le seuil de franchise de TVA se mesure sur votre chiffre d'affaires national total : ne présumez pas que vos factures étrangères en sortent, faites-le confirmer avant de vous croire hors d'atteinte.",
        suite4: "Gardez les factures européennes à part : ce sont elles qui alimentent vos déclarations mensuelles, et c'est sur elles que porte le recoupement.",

        source: "Règles relevées le 8 septembre 2026 auprès de la DGFiP et du BOFiP pour les mentions, et de la douane pour la déclaration européenne de services. La mention de franchise, qui change de code le 1er janvier 2027, a été relue sur Légifrance le 10 septembre 2026.",
    },
    en: {
        phases: ["Your transaction", "Your client", "What applies"],
        retour: "Back",
        prive: "Nothing leaves this tab: the diagnosis runs in your browser, and everything is erased when you close it.",

        sensTitre: "Are you selling, or buying?",
        sensAide: "The rules are not symmetrical: what you owe when selling and what you owe when buying have neither the same thresholds nor the same filings.",
        vends: "I am selling",
        vendsDetail: "A service or a product to a client abroad",
        achete: "I am buying",
        acheteDetail: "A service or goods from a foreign supplier",

        zoneTitre: "Where is the other party established?",
        zoneAide: "« Abroad » and « European » are not the same thing, and that is the most common confusion on this subject.",
        ue: "In the European Union",
        ueDetail: "Germany, Belgium, Spain, Ireland, Italy…",
        horsUe: "Outside the Union",
        horsUeDetail: "Switzerland, United Kingdom, United States, Canada…",

        clientTitre: "Is your client a business?",
        clientAide: "A business reverse-charges the tax at home. A consumer cannot, and the rules change entirely.",
        pro: "A business",
        proDetail: "A company, with a VAT number",
        particulier: "A consumer",
        particulierDetail: "A private individual, with no VAT number",

        natureTitre: "Goods, or a service?",
        natureTitreAchat: "Are you buying goods or a service?",
        natureAide: "The thresholds differ: business-to-business services have none at all.",
        service: "A service",
        serviceDetail: "Consulting, development, design, training, software subscription…",
        bien: "Goods",
        bienDetail: "Merchandise, a physical product, stock",

        cumulTitre: "How much have you accumulated this year?",
        cumulAideAchat: "The total of your purchases of goods in the EU since 1 January. The {seuil} threshold counts over the calendar year, not per order.",
        cumulAideVente: "The total of your sales to consumers in other member states since 1 January. The {seuil} threshold counts over the calendar year.",
        champCumul: "Total since 1 January",
        erreurFormat: "Enter an amount in euros, for example 9500.",

        renvoiTitre: "This situation is one to check with your tax office",
        renvoiHorsUe: "A Swiss, British, American or Canadian client is not an EU client. Neither the intra-EU VAT number nor the European services declaration applies to them, and the rules that do vary with the nature of the transaction and the country. No official source gives a usable general account: have your case confirmed by your business tax office, before issuing the invoice rather than after.",
        renvoiVenteBiensPro: "The tax authority lists three EU situations: your purchases of goods, your business-to-business services, and your distance sales to consumers. Selling goods to a business in another member state is not among them, and no source consulted gives the regime for a seller under the exemption. Have your case confirmed before issuing the invoice.",
        renvoiVenteServicesParticulier: "The tax authority lists three EU situations: your purchases of goods, your business-to-business services, and your distance sales of goods to consumers. A service sold to an EU consumer is not among them. Have your case confirmed by your business tax office before issuing the invoice.",
        renvoiNote: "We would rather tell you we do not know than give you a rule we cannot ground.",
        voirResultat: "See what applies",
        resultatTitre: "What your situation requires",
        recapTitre: "The detail",
        recommencer: "Start again",

        numeroOui: "Intra-EU VAT number required",
        numeroNon: "No VAT number to request yet",

        alerteSeuilProche: "You are nearing the threshold: {reste} left before you cross it. The total counts over the calendar year, and a single order can be enough to pass it.",

        mentionsTitre: "The accepted wordings",
        mentions: "« Autoliquidation » is the wording the tax authority uses, but it is not the only one: the doctrine also accepts a reference to article 196 of directive 2006/112/EC, to article 283-2 of the French tax code that transposes it, accepted until 30 June 2028 like any reference to that code, or any other wording indicating that the transaction is reverse-charged.",

        desTitre: "The declaration nobody asks you for",
        des: "The European services declaration is filed on the customs portal, never on impots.gouv.fr, and by the 10th working day of the following month at the latest. It has no threshold: a €300 invoice calls for it just as much as a €30,000 one. It is monthly, but only for the months in which you invoiced such a service. And it is not a VAT return, it is a summary statement.",

        achatTitre: "What the exemption does not spare you",
        achat: "The tax authority writes that a micro-entrepreneur has no VAT return to file « as a general rule ». That reservation covers precisely these EU transactions. When buying, you become the person liable for French VAT, and you must declare it even under the exemption, even without having collected a single euro of tax.",

        suiteTitre: "What happens next",
        suite1: "The intra-EU VAT number is requested from your business tax office, through the secure messaging of your professional account, and it arrives within a few days. Ask for it before issuing the invoice: it is not granted automatically while you are under the exemption.",
        suite2: "Check your client's number before issuing the invoice: it is what justifies the tax going to them.",
        suite3: "The VAT exemption threshold is measured on your total national turnover: do not assume your foreign invoices fall outside it, have it confirmed before believing yourself out of reach.",
        suite4: "Keep your EU invoices separate: they feed your monthly filings, and they are what the cross-checking is based on.",

        source: "Rules recorded on 8 September 2026 from the French tax authority and its doctrine for the wordings, and from customs for the European services declaration. The exemption wording, which moves to a new code on 1 January 2027, was checked on Légifrance on 10 September 2026.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        sens: "Si vous vendez ou si vous achetez",
        zone: "Où votre interlocuteur est établi",
        client: "Si votre client est un professionnel",
        nature: "S'il s'agit d'un bien ou d'un service",
        cumulAnnuel: "Votre cumul depuis le 1er janvier",
    },
    en: {
        sens: "Whether you are selling or buying",
        zone: "Where the other party is established",
        client: "Whether your client is a business",
        nature: "Whether it is goods or a service",
        cumulAnnuel: "Your total since 1 January",
    },
};

const PHASES_PAR_ECRAN = {
    sens: 0,
    zone: 0,
    client: 1,
    nature: 1,
    renvoi: 1,
    cumul: 2,
    resultat: 2,
};

const VIDE = { sens: "", zone: "", client: "", nature: "", cumulAnnuel: "" };

export default function FacturationEtranger({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const [r, setR] = useState(VIDE);

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "sens",
        () => setR(VIDE),
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    /* La facture est datée du jour que la page pose : c'est cette date qui
       choisit la mention de franchise, 293 B ou L. 233-3 du CIBS. */
    const jour = jourDu(aujourdhui);

    const cas = useMemo(() => situation(r)?.cas || null, [r]);
    const controle = useMemo(() => valider(r), [r]);
    const droits = useMemo(() => obligations(r, jour), [r, jour]);
    const numero = useMemo(() => numeroExige(r), [r]);
    const recap = useMemo(() => recapitulatif(r, jour), [r, jour]);

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const seuil = cas ? seuilDuCas(cas) : null;

    /** Après la nature, on va au refus, au cumul, ou droit au résultat. */
    const suiteApresNature = (choisi) => {
        const prochain = situation({ ...r, nature: choisi })?.cas;
        if (CAS_RENVOYES.includes(prochain)) return "renvoi";
        return seuilDuCas(prochain) === null ? "resultat" : "cumul";
    };

    /* ---------------------------------------------------------------- */
    /* Les trois questions                                               */
    /* ---------------------------------------------------------------- */

    if (etape === "sens") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.sensTitre} aide={t.sensAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="export-linear"
                        titre={t.vends}
                        detail={t.vendsDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, sens: "vends" }));
                            aller("zone");
                        }}
                    />
                    <Choix
                        icone="import-linear"
                        titre={t.achete}
                        detail={t.acheteDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, sens: "achete" }));
                            aller("zone");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "zone") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.zoneTitre} aide={t.zoneAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="global-linear"
                        titre={t.ue}
                        detail={t.ueDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, zone: "ue" }));
                            aller(r.sens === "vends" ? "client" : "nature");
                        }}
                    />
                    <Choix
                        icone="planet-linear"
                        titre={t.horsUe}
                        detail={t.horsUeDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, zone: "horsUe" }));
                            aller("renvoi");
                        }}
                    />
                </div>
            </Ecran>
        );
    }

    if (etape === "client") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.clientTitre} aide={t.clientAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="buildings-2-linear"
                        titre={t.pro}
                        detail={t.proDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, client: "pro" }));
                            aller("nature");
                        }}
                    />
                    <Choix
                        icone="user-rounded-linear"
                        titre={t.particulier}
                        detail={t.particulierDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, client: "particulier" }));
                            aller("nature");
                        }}
                    />
                </div>
            </Ecran>
        );
    }

    if (etape === "nature") {
        const choisir = (valeur) => () => {
            setR((p) => ({ ...p, nature: valeur }));
            aller(suiteApresNature(valeur));
        };
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={r.sens === "achete" ? t.natureTitreAchat : t.natureTitre}
                aide={t.natureAide}
            >
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="case-minimalistic-linear"
                        titre={t.service}
                        detail={t.serviceDetail}
                        onClick={choisir("service")}
                    />
                    <Choix icone="box-minimalistic-linear" titre={t.bien} detail={t.bienDetail} onClick={choisir("bien")} />
                </div>
            </Ecran>
        );
    }

    if (etape === "renvoi") {
        const texte =
            cas === "horsUe"
                ? t.renvoiHorsUe
                : cas === "venteBiensPro"
                  ? t.renvoiVenteBiensPro
                  : t.renvoiVenteServicesParticulier;
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.renvoiTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-4">{texte}</p>
                    <p className="text-sm text-gray-500 dark:text-lightBlue/70 mb-0">{t.renvoiNote}</p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "cumul") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.cumulTitre}
                aide={remplir(r.sens === "achete" ? t.cumulAideAchat : t.cumulAideVente, {
                    seuil: euros(seuil || SEUIL_ACHATS_BIENS),
                })}
            >
                <Champ
                    libelle={t.champCumul}
                    valeur={r.cumulAnnuel}
                    poser={poser("cumulAnnuel")}
                    erreur={
                        (controle.invalides || []).some((i) => i.champ === "cumulAnnuel")
                            ? t.erreurFormat
                            : ""
                    }
                    inputMode="decimal"
                />
                <Avancer
                    pleineLargeur
                    disabled={!String(r.cumulAnnuel || "").trim() || !controle.valide}
                    onClick={() => aller("resultat")}
                >
                    {t.voirResultat}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!recap || !droits ? (
                <div className="mt-4">
                    <ul className="space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 p-5 mb-6">
                        <div className="flex items-center gap-2.5">
                            <SolarIcon
                                name={numero.exige ? "shield-warning-linear" : "shield-check-linear"}
                                className="text-2xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold text-lg dark:text-white leading-snug">
                                {numero.exige ? t.numeroOui : t.numeroNon}
                            </div>
                        </div>
                    </div>

                    {controle.alertes.includes("seuilProche") && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteSeuilProche, { reste: euros(numero.reste) })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {droits.des && (
                        <div className="mb-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                            <div className="flex items-center gap-2.5 mb-3">
                                <SolarIcon
                                    name="calendar-mark-linear"
                                    className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                                />
                                <div className="font-semibold dark:text-white leading-snug">{t.desTitre}</div>
                            </div>
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                {remplir(t.des, { delai: DES_DELAI, portail: DES_PORTAIL })}
                            </p>
                        </div>
                    )}

                    {droits.mentions.length > 1 && (
                        <div className="mb-5 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                            <div className="flex items-center gap-2.5 mb-3">
                                <SolarIcon
                                    name="document-text-linear"
                                    className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                                />
                                <div className="font-semibold dark:text-white leading-snug">{t.mentionsTitre}</div>
                            </div>
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                                {remplir(t.mentions, { admises: mentionsAutoliquidationAdmises(jour).length })}
                            </p>
                        </div>
                    )}

                    {droits.declarationDeTva && (
                        <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4">
                            <div className="flex items-center gap-2.5 mb-2">
                                <SolarIcon name="danger-triangle-linear" className="text-lg shrink-0 text-amber-600" />
                                <div className="font-semibold dark:text-white leading-snug">{t.achatTitre}</div>
                            </div>
                            <p className="text-sm leading-7 text-gray-700 dark:text-lightBlue mb-0">{t.achat}</p>
                        </div>
                    )}

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <Liste items={[t.suite1, t.suite2, t.suite3, t.suite4]} />
                    </div>

                    <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0">{t.source}</p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
